import { Badge } from "@/components/ui/badge";
import { getDonenessBadgeColor } from "@/lib/badge-utils";
import { cn } from "@/lib/utils";
import { useLocalization } from "@/context/localization-context";

/** One output of the crust-strip CNN as the summary table shows it. */
export interface DonenessGaugeData {
  /** Model probability of the positive verdict (burnt / raw), 0..1. */
  probability: number;
  /** Decision threshold on that probability, 0..1. */
  threshold: number;
  /** Verdict the backend recorded (probability >= threshold). */
  positive: boolean;
  tone: "burnt" | "raw";
}

interface DonenessMiniGaugeProps extends DonenessGaugeData {
  label: string;
  className?: string;
  /** Hover hooks (the evaluation views show this output's heatmap meanwhile). */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const TONES = {
  burnt: { bar: "bg-red-500", badge: "burnt" as const },
  raw: { bar: "bg-orange-500", badge: "raw" as const },
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
/** CSS length along a bar */
const percent = (value: number, digits = 0) => `${(value * 100).toFixed(digits)}%`;
/** a probability as text in the UI language ("12.5%" / "12,5 %") */
function usePercentText() {
  const { formatNumber } = useLocalization();
  return (value: number, digits = 0) =>
    formatNumber(value, { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits });
}

interface ThresholdBarProps {
  /** Model probability, 0..1. */
  probability: number;
  /** Decision threshold on that probability, 0..1. */
  threshold: number;
  /** Tailwind background class of the filled part. */
  barClassName: string;
  /** Value at the bar's right end (default 1): a threshold of a few percent needs a shorter scale to be read. */
  max?: number;
  className?: string;
}

/** Thin probability bar with the decision threshold marked and labelled. */
export function ThresholdBar({
  probability,
  threshold,
  barClassName,
  max = 1,
  className,
}: ThresholdBarProps) {
  const { t } = useLocalization();
  const percentText = usePercentText();
  const scale = max > 0 ? max : 1;
  // positions along the bar; the title and the caption keep the real values
  const p = clamp01(probability / scale);
  const at = clamp01(threshold / scale);
  // keep the threshold caption inside the bar near either edge
  const captionTransform =
    at > 0.8 ? "translateX(-100%)" : at < 0.2 ? "none" : "translateX(-50%)";

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div
        className="relative h-1.5 w-full rounded-full bg-muted"
        title={t("detail.gauge.vsThreshold", {
          value: percentText(probability, 1),
          threshold: percentText(threshold),
        })}
      >
        <div
          className={cn("h-1.5 rounded-full", barClassName)}
          style={{ width: percent(p, 2) }}
        />
        <div
          className="absolute -top-[3px] h-3 w-0.5 bg-foreground/70"
          style={{ left: `calc(${percent(at, 2)} - 1px)` }}
        />
      </div>

      <div className="relative h-3 text-[10px] leading-3 text-muted-foreground">
        <span
          className="absolute whitespace-nowrap"
          style={{ left: percent(at, 2), transform: captionTransform }}
        >
          {t("detail.gauge.threshold", { value: percentText(threshold) })}
        </span>
      </div>
    </div>
  );
}

/**
 * Compact version of the developer-mode OutputGauge (burnt-model-view.tsx)
 * sized for a summary-table row: probability, verdict badge and a thin bar
 * with the decision threshold marked and labelled.
 */
export function DonenessMiniGauge({
  label,
  probability,
  threshold,
  positive,
  tone,
  className,
  onMouseEnter,
  onMouseLeave,
}: DonenessMiniGaugeProps) {
  const { t } = useLocalization();
  const percentText = usePercentText();
  const p = clamp01(probability);
  const { bar, badge } = TONES[tone];

  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-md",
        onMouseEnter && "cursor-pointer",
        className,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn("text-sm", positive ? "font-medium text-foreground" : "text-foreground/80")}>
          {label}
        </span>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-sm tabular-nums",
              positive ? "font-semibold text-red-300" : "text-muted-foreground",
            )}
          >
            {percentText(p, 1)}
          </span>
          {positive && (
            <Badge
              className={cn(
                "px-1.5 py-0 text-[10px] leading-4",
                getDonenessBadgeColor(badge),
              )}
            >
              {t("status.nok")}
            </Badge>
          )}
        </div>
      </div>

      {/* below the threshold the bar is neutral: only a verdict over it is coloured */}
      <ThresholdBar
        probability={probability}
        threshold={threshold}
        barClassName={positive ? bar : "bg-muted-foreground/45"}
      />
    </div>
  );
}

export default DonenessMiniGauge;
